/**
 * Invoice utility functions
 */

/**
 * Formats an invoice display name with customer and branch information
 *
 * @param {Object} invoice - Invoice object with invoice_no, customer, and optionally branch
 * @param {string} invoice.invoice_no - The invoice number (e.g., "INV-042")
 * @param {Object} invoice.customer - Customer object
 * @param {string} invoice.customer.name - Customer name
 * @param {Object} [invoice.branch] - Optional branch object (null for main company)
 * @param {string} [invoice.branch.name] - Branch name
 *
 * @returns {string} Formatted display name (e.g., "Invoice INV-042 - The Prancing Stag Edinburgh")
 *
 * @example
 * // With branch
 * formatInvoiceDisplayName({
 *   invoice_no: "INV-042",
 *   customer: { name: "The Prancing Stag" },
 *   branch: { name: "Edinburgh" }
 * })
 * // Returns: "Invoice INV-042 - The Prancing Stag Edinburgh"
 *
 * @example
 * // Without branch (main company)
 * formatInvoiceDisplayName({
 *   invoice_no: "INV-042",
 *   customer: { name: "The Prancing Stag" },
 *   branch: null
 * })
 * // Returns: "Invoice INV-042 - The Prancing Stag"
 */
export const formatInvoiceDisplayName = (invoice) => {
  if (!invoice || !invoice.customer) {
    return 'Invoice'
  }

  // Prefer Xero invoice number from mapping, fallback to Odin invoice_no
  const invoiceNumber = invoice.xero_mapping?.[0]?.xero_invoice_number
    || invoice.invoice_no
    || 'Unknown'

  const customerName = invoice.customer.name || 'Unknown Customer'
  const branchName = invoice.branch?.name ? ` ${invoice.branch.name}` : ''

  return `Invoice ${invoiceNumber} - ${customerName}${branchName}`
}

/**
 * Formats an invoice display name from invoice data (for PDFs and previews)
 *
 * @param {Object} invoiceData - Invoice data object
 * @param {string} invoiceData.invoiceNumber - The invoice number
 * @param {Object} invoiceData.customer - Customer object
 * @param {string} invoiceData.customer.name - Customer name
 * @param {Array} [invoiceData.branches] - Array of branch objects
 *
 * @returns {string} Formatted display name
 */
export const formatInvoiceDataDisplayName = (invoiceData) => {
  if (!invoiceData || !invoiceData.invoiceNumber || !invoiceData.customer) {
    return 'Invoice'
  }

  const customerName = invoiceData.customer.name || 'Unknown Customer'

  // For invoice data with branches array, use the first branch if it exists
  const branchName = invoiceData.branches?.[0]?.name && invoiceData.branches[0].name !== 'Main Company'
    ? ` ${invoiceData.branches[0].name}`
    : ''

  return `Invoice ${invoiceData.invoiceNumber} - ${customerName}${branchName}`
}
